import ButtonWrapper from '@components/common/ButtonWrapper';
import styles from '../components/styles/Activity.module.scss';
import { initSelectors } from '@behavior/init';
import useUser from '@behavior/init/getUserV2';
import { useTranslation } from 'react-i18next';
import { CLONE_MODE, EDIT_MODE, entityType, VIEW_MODE } from '@src/constants';
import { ActivityState } from '../types/ActivityStates';
import usePostActivity from '@behavior/activity/usePostActivity';
import {
  prepareAndSendActivityDTO,
  prepareAndSendRiskAssessmentDTO,
  prepareAndSendCloneActivityDTO,
} from '../activityUtils';
import { RiskAssessmentState } from '../types/RiskAssessmentState';
import { RiskAssessmentDTO } from '@behavior/activity/dto/RiskAssessmentDTO';
import usePostRiskAssessment from '@behavior/activity/usePostRiskAssessment';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { TraceabilityState } from '../types/TraceabilityState';
import { ConfirmModal } from 'react-lib/dist/components-es';
import { Button, Confirm } from 'semantic-ui-react';
import useDeleteActivity from '@behavior/activity/useDeleteActivity';
import { QueryKey } from '@behavior/QueryKey';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadActivityReport } from '@behavior/activity/api';
import { ActionState } from '../types/ActionState';
import usePostCloneActivity from '@behavior/activity/usePostCloneActivity';
import { ActivityTraceabilityDTO } from '@behavior/activity/dto/ActivityTraceabilityDTO';
import usePostReleaseActivity from '@behavior/activity/usePostReleaseActivity';
import usePostReleaseActions from '@behavior/activity/usePostReleaseActions';
import { ActivityStatus } from '../types/ActivityStatus';
import { Popup } from 'semantic-ui-react';
import TraceabilityDetails from '@components/common/TraceabilityDetails';
import { isActivityCanCreate } from '@behavior/init/selectors';
import { handleOnClickReportDownload } from '@src/reports/common/Util';
import ActivityReport from '@src/reports/activity/ActivityReport';

type Props = {
  entityResponsible: number[];
  entityCreator?: number;
  history: any;
  id: number;
  riskAssessment?: RiskAssessmentDTO;
  mode: string;
  activityState: ActivityState;
  riskAssessmentState: RiskAssessmentState;
  actionState: ActionState;
  actions: ActionDTO[];
  activityTraceabilities?: ActivityTraceabilityDTO;
  traceabilityState: TraceabilityState;
  methods?: any;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setSaved: (val: boolean) => void;
  getActivityReport?: (id: string) => void;
};

const OptionsButtonGroup = ({
  entityResponsible,
  entityCreator,
  history,
  id,
  mode,
  activityState,
  riskAssessmentState,
  riskAssessment,
  actionState,
  actions,
  activityTraceabilities,
  traceabilityState,
  methods,
  setIsLoading,
  setSaved,
}: Props) => {
  const { t } = useTranslation();
  const { postReleaseActivity, isLoadingReleaseActivity } = usePostReleaseActivity(id);
  const { postReleaseActions, isLoadingReleaseActions } = usePostReleaseActions(id);
  const { deleteActivityMutate, isLoadingDeleteActivity } = useDeleteActivity(id);
  const { postActivity, isLoadingPostActivity } = usePostActivity();
  const { postCloneActivity, isLoadingCloneActivity } = usePostCloneActivity();
  const { postRiskAssessment, isLoadingPostRiskAssessment } = usePostRiskAssessment();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  let isEditor = initSelectors.isEditor(user);
  let isCanModifyAsCreator = initSelectors.isCanModifyAsCreator(
    'activity',
    user,
    entityResponsible,
    entityCreator
  );

  useEffect(() => {
    setIsLoading(
      isLoadingReleaseActivity ||
        isLoadingReleaseActions ||
        isLoadingCloneActivity ||
        isLoadingDeleteActivity ||
        isLoadingPostActivity ||
        isLoadingPostRiskAssessment
    );
  }, [
    isLoadingReleaseActivity,
    isLoadingReleaseActions,
    isLoadingCloneActivity,
    isLoadingDeleteActivity,
    isLoadingPostActivity,
    isLoadingPostRiskAssessment,
  ]);

  const onSubmit = (data: any) => {
    onUpdate();
    setSaved(true);
    toast.success(t('message.title.success.post.traceability'));
  };

  const handleEdit = () => {
    methods.handleSubmit(onSubmit)();
    setOpen(false);
  };

  const handleModalOpen = () => {
    setOpen(true);
  };

  const releaseActivity = () => {
    setIsLoading(true);
    postReleaseActivity().then(activity => {
      setIsLoading(false);
      activityState.setActivity(activity);
    });
  };

  const releaseActions = () => {
    setIsLoading(true);
    postReleaseActions().then(activity => {
      setIsLoading(false);
      activityState.setActivity(activity);
    });
  };

  const onUpdate = () => {
    if (activityState) {
      prepareAndSendActivityDTO({
        activityState,
        postActivity,
      });
    }
    if (riskAssessmentState) {
      const updatedTraceability: ActivityTraceabilityDTO = {
        ...activityTraceabilities,
        hseUnit: traceabilityState?.hseUnit,
        signatureDte: traceabilityState?.signatureDate,
        riskAssessment,
        distributionList: traceabilityState?.distributionList,
        traceabilityAssessor: traceabilityState?.assessorList,
      };

      prepareAndSendRiskAssessmentDTO({
        riskAssessment,
        riskAssessmentState,
        postRiskAssessment,
        activity: activityState.activity,
        actions,
        activityTraceabilities: updatedTraceability,
        mode,
      });
    }
  };

  const onDelete = () => {
    setIsLoading(true);
    deleteActivityMutate();
  };

  const handleClone = () => {
    if (riskAssessmentState) {
      setIsLoading(true);
      prepareAndSendCloneActivityDTO({
        activityState,
        riskAssessmentState,
        activityTraceabilities,
        actions,
        postCloneActivity,
        setIsLoading,
        traceabilityState,
      });
    }
  };

  const getReleaseButtons = (activityState: ActivityState) => {
    switch (activityState?.activity?.status) {
      case ActivityStatus.CREATION:
        return (
          <>
            <ButtonWrapper
              key="releaseActions"
              type="button"
              icon="send"
              label={t('details.page.action.release.button')}
              color="green"
              className={styles.editButton}
              size="mini"
              onClick={handleModalOpen}
            />
            <Confirm
              key="releaseActions"
              open={open}
              header={t(`confirm.modal.release.actions.title`)}
              content={t(`confirm.modal.release.actions.content`)}
              cancelButton={t`confirm.modal.release.actions.cancel`}
              confirmButton={t`confirm.modal.release.actions.confirm`}
              onConfirm={() => releaseActions()}
              onCancel={() => setOpen(false)}
            />
          </>
        );

      case ActivityStatus.ACTION_RELEASE:
        return (
          <>
            <ButtonWrapper
              key="releaseActivity"
              type="button"
              icon="send"
              label={t('details.page.activity.release.button')}
              color="teal"
              className={styles.editButton}
              size="mini"
              onClick={handleModalOpen}
            />
            <Confirm
              key="releaseActivity"
              open={open}
              header={t(`confirm.modal.release.activity.title`)}
              content={t(`confirm.modal.release.activity.content`)}
              cancelButton={t`confirm.modal.release.activity.cancel`}
              confirmButton={t`confirm.modal.release.activity.confirm`}
              onConfirm={() => releaseActivity()}
              onCancel={() => setOpen(false)}
            />
          </>
        );

      default:
        return <></>;
    }
  };

  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const isActivityAccessible =
    user && isActivityCanCreate(user, entityType.activity) && isCanModifyAsCreator;

  const getActivityReport = () => {
    return (
      <ActivityReport
        activityState={activityState}
        actionState={actionState}
        traceabilityState={traceabilityState}
        riskAssessmentState={riskAssessmentState}
      />
    );
  };
  return (
    <>
      {isActivityAccessible && mode === VIEW_MODE && (
        <div className={styles.buttonGroupActivity}>
          <div>
            <ButtonWrapper
              type="button"
              key="export.pdf.button"
              icon="file pdf"
              onClick={() => {
                handleOnClickReportDownload(getActivityReport(), setIsDownloadingReport);
              }}
              label={t('details.page.export.pdf.button.tooltip')}
              disabled={isDownloadingReport}
            />
          </div>
          <div className={styles.editButtonDiv}>
            <Popup
              wide="very"
              key="showTraceabilityDetailsButtonKey"
              content={<TraceabilityDetails traceableEntity={activityState.activity} />}
              on={['hover', 'click']}
              position="bottom right"
              trigger={<ButtonWrapper type="button" icon="info circle" />}
            />
            <ButtonWrapper
              type="button"
              icon="edit"
              key="editActivityButtonKey"
              label={t('details.page.edit.button')}
              primary
              className={styles.editButton}
              size="mini"
              onClick={() => history.push(`/${QueryKey.ACTIVITY}/${EDIT_MODE}/${id}`)}
            />
            {getReleaseButtons(activityState)}
          </div>
        </div>
      )}
      {isActivityAccessible && mode === EDIT_MODE && (
        <div className={styles.buttonGroupActivity}>
          <div>
            <ButtonWrapper
              type="button"
              key="export.pdf.button"
              icon="file pdf"
              onClick={() => {
                downloadActivityReport(id);
              }}
              label={t('details.page.export.pdf.button.tooltip')}
            />
          </div>
          <div className={styles.editButtonDiv}>
            <Popup
              wide="very"
              key="showTraceabilityDetailsButtonKey"
              content={<TraceabilityDetails traceableEntity={activityState.activity} />}
              on={['hover', 'click']}
              position="bottom right"
              trigger={<ButtonWrapper type="button" icon="info circle" />}
            />
            <ConfirmModal
              buttonProps={{
                color: 'red',
                icon: 'trash alternate',
                size: 'mini',
                className: `${styles.buttonGroup}`,
              }}
              key="deleteEntityButtonKey"
              header={t(`confirm.modal.delete.activity.title`)}
              content={t(`confirm.modal.delete.activity.content`)}
              className="mini"
              confirmFunction={onDelete}
            />
            <ButtonWrapper
              key="update"
              icon="save"
              type="button"
              label={t('details.page.save.button')}
              primary
              size="mini"
              onClick={handleModalOpen}
              className={styles.buttonGroup}
            />
            <Confirm
              key="update"
              open={open}
              header={t(`confirm.modal.edit.activity.title`)}
              content={t(`confirm.modal.edit.activity.content`)}
              cancelButton={t`confirm.modal.edit.activity.cancel`}
              confirmButton={t`confirm.modal.edit.activity.confirm`}
              onConfirm={() => handleEdit()}
              onCancel={() => setOpen(false)}
            />
          </div>
        </div>
      )}

      {mode === CLONE_MODE && (
        <div className={styles.editButtonDiv}>
          <ButtonWrapper
            type="button"
            icon="save"
            key="editActivityButtonKey"
            label={t('details.page.clone.button')}
            primary
            className={styles.editButton}
            size="mini"
            onClick={handleClone}
          />
        </div>
      )}
    </>
  );
};

export default OptionsButtonGroup;
